import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  exportPartnerChannel,
  proposePartnerRetention,
  resolvePartnerPolicy,
  setPartnerLegalHold,
  updatePartnerChannel,
} from "@/lib/partnerApi";
import { toastError, toastSuccess } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const PARTNER_RETENTION_OPTIONS = [
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 180, label: "180 days" },
  { value: 365, label: "365 days" },
  { value: null, label: "Indefinite" },
];

export function formatRetentionDays(days) {
  if (days == null) return "Indefinite";
  return `${days} days`;
}

/**
 * Shared partner-channel settings: rename, retention (dual-consent), legal hold
 * (one-party place / propose-accept release), and optional export.
 */
export default function PartnerChannelSettingsForm({
  channel,
  orgId,
  channelId,
  accessToken,
  onChannelChange,
  showExport = true,
  showEndPartnershipHint = false,
  onEndPartnershipHintClick,
}) {
  const [busy, setBusy] = useState(false);
  const [titleDraft, setTitleDraft] = useState(channel?.title || "");

  useEffect(() => {
    setTitleDraft(channel?.title || "");
  }, [channel?.title, channel?.channel_id]);

  const pending = channel?.pending_policy || null;
  const pendingRelease = channel?.pending_legal_hold_release || null;
  const proposedByUs = pending?.proposed_by_org_id === orgId;
  const releaseProposedByUs = pendingRelease?.proposed_by_org_id === orgId;
  const partnerName = channel?.partner_org?.name || "the partner organization";
  const holdActive = !!channel?.legal_hold;
  const holdByUs = channel?.legal_hold_by_org_id === orgId;

  const run = async (fn, successMessage) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.channel) onChannelChange?.(res.channel);
      if (successMessage) toastSuccess(successMessage);
      return res;
    } catch (err) {
      toastError(err, "Could not update channel settings");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const saveTitle = () => {
    const next = titleDraft.trim();
    if (!next || next === channel?.title) return;
    run(
      () => updatePartnerChannel(accessToken, orgId, channelId, { title: next }),
      "Channel renamed"
    );
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor={`partner-title-${channelId}`} className="text-sm font-medium">
          Channel name
        </Label>
        <p className="text-xs text-muted-foreground">
          Either organization can rename this channel. Changes apply for both
          sides immediately.
        </p>
        <div className="flex gap-2">
          <Input
            id={`partner-title-${channelId}`}
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            maxLength={120}
            className="h-9"
            disabled={busy}
          />
          <Button
            type="button"
            size="sm"
            disabled={
              busy ||
              !titleDraft.trim() ||
              titleDraft.trim() === (channel?.title || "")
            }
            onClick={saveTitle}
          >
            Save
          </Button>
        </div>
      </div>

      <div className="space-y-2 border-t border-border pt-4">
        <p className="text-sm font-medium">
          Retention ·{" "}
          <span className="text-muted-foreground font-normal">
            {formatRetentionDays(channel?.retention_days)}
          </span>
        </p>
        {pending ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {proposedByUs ? "You proposed" : `${partnerName} proposed`}{" "}
              {formatRetentionDays(pending.proposed_retention_days)}.
              {proposedByUs
                ? ` Waiting on ${partnerName}.`
                : " Accept or reject to settle it."}
            </p>
            {proposedByUs ? null : (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    run(
                      () =>
                        resolvePartnerPolicy(
                          accessToken,
                          orgId,
                          channelId,
                          pending.proposal_id,
                          true
                        ),
                      "Retention updated"
                    )
                  }
                >
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() =>
                    run(
                      () =>
                        resolvePartnerPolicy(
                          accessToken,
                          orgId,
                          channelId,
                          pending.proposal_id,
                          false
                        ),
                      "Proposal rejected"
                    )
                  }
                >
                  Reject
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {PARTNER_RETENTION_OPTIONS.filter(
              (o) => o.value !== (channel?.retention_days ?? null)
            ).map((o) => (
              <Button
                key={String(o.value)}
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() =>
                  run(
                    () =>
                      proposePartnerRetention(
                        accessToken,
                        orgId,
                        channelId,
                        o.value
                      ),
                    `Proposed ${o.label.toLowerCase()} to ${partnerName}`
                  )
                }
              >
                Propose {o.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2 border-t border-border pt-4">
        <p className="text-sm font-medium">Legal hold</p>
        <p className="text-xs text-muted-foreground">
          Freezes messaging and pauses retention purges. Either org can place a
          hold; releasing it needs both organizations to agree.
          {holdActive
            ? holdByUs
              ? " Your organization placed this hold."
              : ` Placed by ${partnerName}.`
            : ""}
        </p>
        {holdActive ? (
          <div className="space-y-2">
            <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
              Channel is frozen while legal hold is active.
            </p>
            {pendingRelease ? (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  {releaseProposedByUs
                    ? `You proposed releasing the hold. Waiting on ${partnerName}.`
                    : `${partnerName} proposed releasing the hold. Accept or reject to settle it.`}
                </p>
                {releaseProposedByUs ? null : (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        run(
                          () =>
                            resolvePartnerPolicy(
                              accessToken,
                              orgId,
                              channelId,
                              pendingRelease.proposal_id,
                              true
                            ),
                          "Legal hold cleared"
                        )
                      }
                    >
                      Accept release
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        run(
                          () =>
                            resolvePartnerPolicy(
                              accessToken,
                              orgId,
                              channelId,
                              pendingRelease.proposal_id,
                              false
                            ),
                          "Release rejected — hold remains"
                        )
                      }
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() =>
                  run(
                    () =>
                      setPartnerLegalHold(accessToken, orgId, channelId, false),
                    `Release proposed to ${partnerName}`
                  )
                }
              >
                Propose release
              </Button>
            )}
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() =>
              run(
                () => setPartnerLegalHold(accessToken, orgId, channelId, true),
                "Legal hold set — channel frozen"
              )
            }
          >
            Place legal hold
          </Button>
        )}
      </div>

      {showExport ? (
        <div className="space-y-2 border-t border-border pt-4">
          <p className="text-sm font-medium">Export</p>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() =>
              run(async () => {
                const blob = await exportPartnerChannel(
                  accessToken,
                  orgId,
                  channelId
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `partner-channel-${channelId}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }, "Transcript downloaded")
            }
          >
            Export transcript
          </Button>
        </div>
      ) : null}

      {showEndPartnershipHint ? (
        <p className="text-xs text-muted-foreground border-t border-border pt-4">
          Ending the partnership lives in{" "}
          <Link
            to={`/orgs/${encodeURIComponent(orgId)}/settings?section=partners`}
            className="underline underline-offset-2 hover:text-foreground"
            onClick={onEndPartnershipHintClick}
          >
            Settings → Partner spaces
          </Link>
          .
        </p>
      ) : null}
    </div>
  );
}
